import React, { useEffect, useMemo, useRef } from 'react';
import Column from '../../../core/layout/Column';
import Select from '../../../core/form/Select';
import get from 'lodash/get';
import MaskInput from '../../../core/form/MaskInput';
import Button from '../../../core/form/Button';
import Row from '../../../core/layout/Row';
import TextInput from '../../../core/form/TextInput';
import PropTypes from 'prop-types';
import { CONTRACT_PRODUCTS, CURRENCY_MASK_OPTIONS } from '../../../core/consts';

const BillProductsForm = (props) => {
  const maskInputRef = useRef();

  const hasToShowDiscountField = useMemo(() => {
    if (get(props.values, 'hs_sku', '') === CONTRACT_PRODUCTS.CUSTOM_PACKAGE) return true;
    if (get(props.values, 'hs_sku', '') === CONTRACT_PRODUCTS.EXTRA_LICENSE_SEAT) {
      return !!get(props.values, 'discount', null);
    }

    return false;
  }, [props.values]);

  useEffect(() => {
    maskInputRef.current.updateMaskOptions(CURRENCY_MASK_OPTIONS[props.currencyType]);
  }, [props]);

  return (
    <Row>
      <Column size={3}>
        <Select
          onChange={(value) => props.onChange('hs_sku', value)}
          options={props.options}
          label="Product"
          required
          showSearch
          disabled={props.disabled}
          value={get(props.values, 'hs_sku', null)}
          errorText={get(props.errors, 'hs_sku', '')}
        />
      </Column>
      <Column size={2}>
        <TextInput
          label="Description"
          value={get(props.values, 'description', null)}
          onChange={(value) => props.onChange('description', value)}
          disabled={props.disabled}
          required={get(props.values, 'hs_sku', '') === 'others'}
          errorText={get(props.errors, 'description', '')}
        />
      </Column>
      <Column size={2}>
        <MaskInput
          disabled={props.disabled}
          label="Amount"
          value={get(props.values, 'amount', null)}
          onChange={(value) => props.onChange('amount', value)}
          blocks={[5]}
          numericOnly
        />
      </Column>
      <Column size={2}>
        <MaskInput
          disabled={props.disabled}
          label={`Unit Price (${CURRENCY_MASK_OPTIONS[props.currencyType].symbol})`}
          required
          value={get(props.values, 'unitPrice', null)}
          onChange={(value) => props.onChange('unitPrice', value)}
          errorText={get(props.errors, 'unitPrice', '')}
          delimiter={CURRENCY_MASK_OPTIONS[props.currencyType].delimiter}
          numeralDecimalMark={CURRENCY_MASK_OPTIONS[props.currencyType].numeralDecimalMark}
          maskType="numeral"
          ref={maskInputRef}
        />
      </Column>
      {hasToShowDiscountField && (
        <Column size={1}>
          <MaskInput
            disabled={props.disabled}
            label="Discount (%)"
            required
            value={get(props.values, 'discount', null)}
            onChange={(value) => props.onChange('discount', value)}
            errorText={get(props.errors, 'discount', '')}
            numeralDecimalMark=","
            delimiter=""
            numeralDecimalScale={4}
            numeralPositiveOnly
            maskType="numeral"
          />
        </Column>
      )}
      <Column size={hasToShowDiscountField ? 1 : 2}>
        <MaskInput
          disabled={props.disabled}
          label="Installment Number"
          value={get(props.values, 'installmentNumber', null)}
          onChange={(value) => props.onChange('installmentNumber', value)}
          blocks={[2]}
          numericOnly
        />
      </Column>
      <Column size={1}>
        <div style={{ marginTop: 42 }}>
          <Button
            icon={get(props.values, 'id', null) ? '' : 'plus'}
            label={get(props.values, 'id', null) ? 'Update' : 'Add'}
            onClick={props.onSubmit}
            disabled={props.disabled}
          />
        </div>
      </Column>
    </Row>
  );
};

BillProductsForm.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      hs_sku: PropTypes.string,
      description: PropTypes.string,
      amount: PropTypes.number,
    })
  ),
  onChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  values: PropTypes.object,
  errors: PropTypes.object,
  disabled: PropTypes.bool,
  currencyType: PropTypes.oneOf(['real', 'dollar']),
};

BillProductsForm.defaultProps = {
  values: {},
  errors: {},
  disabled: false,
  currencyType: 'real',
};

export default BillProductsForm;
